import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import {
  GetAllDiscountsApi,
  GetDiscountsByUserApi,
  DeleteDirectDiscountApi,
} from "../../../ApiRequests";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import { usePageRefresh } from "../../../hooks/usePageRefresh";
import { formatDateTime } from "../../../utils/dateTime";
import EditDirectDiscountModal from "../../../components/Modals/EditDirectDiscountModal";
import DeleteModal from "../../../components/Modals/DeleteModal";

const getAppBasePath = () =>
  Number(localStorage.getItem("role")) === 1 ? "/admin" : "/branch";

const formatMoney = (n) =>
  new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
  }).format(Number(n) || 0);

const rowDate = (row) => {
  if (row?.date != null) {
    const sec = typeof row.date === "number" ? row.date : Number(row.date);
    if (!Number.isNaN(sec) && sec > 0) return formatDateTime(sec);
  }
  if (row?.createdAt) return formatDateTime(row.createdAt);
  return "—";
};

/** Works for nested discount.logs and DirectDiscountLog rows. */
const formatLogLine = (entry) => {
  if (!entry) return "";
  const when = entry.at ? formatDateTime(entry.at) : "";
  const byName = entry.performedByName || entry.performedByName || "";
  const by = byName ? ` · by ${byName}` : "";
  if (entry.action === "create") {
    return `Created ${when}${
      entry.after?.amount != null ? ` · ${formatMoney(entry.after.amount)}` : ""
    }${
      entry.after?.desc ? ` · "${entry.after.desc}"` : ""
    }${by}`;
  }
  if (entry.action === "edit") {
    const from =
      entry.before?.amount != null ? formatMoney(entry.before.amount) : "—";
    const to =
      entry.after?.amount != null ? formatMoney(entry.after.amount) : "—";
    const descChanged =
      (entry.before?.desc || "") !== (entry.after?.desc || "");
    const descPart = descChanged
      ? ` · desc "${entry.before?.desc || ""}" → "${entry.after?.desc || ""}"`
      : "";
    return `Edited ${when} · ${from} → ${to}${descPart}${by}`;
  }
  if (entry.action === "delete") {
    return `Deleted ${when}${
      entry.before?.amount != null
        ? ` · ${formatMoney(entry.before.amount)}`
        : ""
    }${by}`;
  }
  return entry.action || "";
};

const DiscountLogPage = () => {
  const { customerId, companyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [removedRows, setRemovedRows] = useState([]);
  const [changeLogs, setChangeLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const mode = useMemo(() => {
    if (customerId)
      return { type: "customer", id: customerId, label: "Customer" };
    if (companyId) return { type: "company", id: companyId, label: "Company" };
    return { type: "all", id: null, label: "All" };
  }, [customerId, companyId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (mode.type === "all") {
        const res = await GetAllDiscountsApi();
        const payload = res?.data?.data?.payload;
        if (Array.isArray(payload)) {
          setRows(payload);
          setRemovedRows([]);
          setChangeLogs([]);
        } else {
          setRows(Array.isArray(payload?.discounts) ? payload.discounts : []);
          setRemovedRows(
            Array.isArray(payload?.deleted) ? payload.deleted : []
          );
          setChangeLogs(
            Array.isArray(payload?.changeLogs) ? payload.changeLogs : []
          );
        }
      } else {
        const userType = mode.type === "customer" ? 1 : 2;
        const res = await GetDiscountsByUserApi(userType, mode.id);
        const payload = res?.data?.data?.payload;
        setRows(Array.isArray(payload?.discounts) ? payload.discounts : []);
        setRemovedRows(Array.isArray(payload?.deleted) ? payload.deleted : []);
        setChangeLogs(
          Array.isArray(payload?.changeLogs) ? payload.changeLogs : []
        );
      }
    } catch (e) {
      setRows([]);
      setRemovedRows([]);
      setChangeLogs([]);
      ErrorToast(
        e.response?.data?.error?.msg || "Could not load discount log"
      );
    } finally {
      setLoading(false);
    }
  }, [mode.type, mode.id]);

  useEffect(() => {
    load();
  }, [load]);

  usePageRefresh(load, [mode.type, mode.id]);

  const discountLogColumns = useMemo(() => {
    if (mode.type === "all") {
      return [
        { id: "user_type" },
        { id: "customerId" },
        { id: "companyId" },
        { id: "date" },
        { id: "amount" },
        { id: "desc" },
      ];
    }
    return [{ id: "date" }, { id: "amount" }, { id: "desc" }];
  }, [mode.type]);

  const filteredRows = useMemo(
    () => filterTableRowsBySearch(rows, search, discountLogColumns),
    [rows, search, discountLogColumns]
  );

  const filteredRemoved = useMemo(
    () => filterTableRowsBySearch(removedRows, search, discountLogColumns),
    [removedRows, search, discountLogColumns]
  );

  const filteredChangeLogs = useMemo(() => {
    if (!search.trim()) return changeLogs;
    const q = search.trim().toLowerCase();
    return changeLogs.filter((log) => {
      const name =
        log.user_type === 1
          ? log.customerId?.name || ""
          : log.companyId?.name || "";
      const hay = [
        log.action,
        name,
        log.performedByName || log.performedByName,
        log.before?.amount,
        log.after?.amount,
        log.before?.desc,
        log.after?.desc,
        formatLogLine(log),
      ]
        .filter((v) => v != null)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [changeLogs, search]);

  const title =
    mode.type === "all" ? "Discount log" : `${mode.label} discount log`;

  const handleDelete = async () => {
    if (!deleteRow?._id) return;
    setDeleteLoading(true);
    try {
      const res = await DeleteDirectDiscountApi(deleteRow._id);
      if (res.data.success) {
        SuccessToast("Discount removed");
        setDeleteRow(null);
        await load();
      } else {
        ErrorToast(res.data.error?.msg || "Delete failed");
      }
    } catch (e) {
      ErrorToast(
        e.response?.data?.error?.msg || e.message || "Delete failed"
      );
    }
    setDeleteLoading(false);
  };

  const renderInlineLogs = (row) => {
    const logs = Array.isArray(row.logs) ? row.logs : [];
    if (logs.length === 0) return <span className="text-muted">—</span>;
    return (
      <ul className="space-y-0.5 text-[11px] text-inkSecondary list-none m-0 p-0">
        {logs.map((entry, idx) => (
          <li key={`${row._id}-log-${idx}`}>{formatLogLine(entry)}</li>
        ))}
      </ul>
    );
  };

  const renderDiscountTable = (data, heading, { allowActions } = {}) => (
    <div className="w-full mt-4">
      <h2 className="text-lg font-bold text-main font-quicksand mb-2">
        {heading}
      </h2>
      {data.length === 0 ? (
        <p className="text-muted text-sm py-4">No entries.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line shadow-soft">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-main text-white">
                {mode.type === "all" && (
                  <th className="border border-main px-2 py-2 text-left">
                    Type
                  </th>
                )}
                {mode.type === "all" && (
                  <th className="border border-main px-2 py-2 text-left">
                    Name
                  </th>
                )}
                <th className="border border-main px-2 py-2 text-left">Date</th>
                <th className="border border-main px-2 py-2 text-right">
                  Amount
                </th>
                <th className="border border-main px-2 py-2 text-left">
                  Description
                </th>
                <th className="border border-main px-2 py-2 text-left">
                  History
                </th>
                {allowActions && (
                  <th className="border border-main px-2 py-2 text-center">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row._id} className="font-quicksand hover:bg-subtle">
                  {mode.type === "all" && (
                    <td className="border border-line px-2 py-1.5 text-inkSecondary">
                      {row.user_type === 1 ? "Customer" : "Company"}
                    </td>
                  )}
                  {mode.type === "all" && (
                    <td className="border border-line px-2 py-1.5 font-medium text-ink">
                      {row.user_type === 1
                        ? row.customerId?.name || "—"
                        : row.companyId?.name || "—"}
                    </td>
                  )}
                  <td className="border border-line px-2 py-1.5 whitespace-nowrap text-inkSecondary">
                    {rowDate(row)}
                  </td>
                  <td className="border border-line px-2 py-1.5 text-right font-semibold text-ink">
                    {formatMoney(row.amount)}
                  </td>
                  <td className="border border-line px-2 py-1.5 max-w-md text-inkSecondary">
                    {row.desc || "—"}
                  </td>
                  <td className="border border-line px-2 py-1.5 max-w-xs align-top">
                    {renderInlineLogs(row)}
                  </td>
                  {allowActions && (
                    <td className="border border-line px-2 py-1.5 text-center whitespace-nowrap">
                      <button
                        type="button"
                        className="text-main font-bold text-xs mr-2 hover:underline"
                        onClick={() => setEditRow(row)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-600 font-bold text-xs hover:underline"
                        onClick={() => setDeleteRow(row)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderChangeLogsTable = () => (
    <div className="w-full mt-8">
      <h2 className="text-lg font-bold text-main font-quicksand mb-2">
        Change activity (create / edit / delete)
      </h2>
      {filteredChangeLogs.length === 0 ? (
        <p className="text-muted text-sm py-4">No change logs yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line shadow-soft">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-main text-white">
                <th className="border border-main px-2 py-2 text-left">When</th>
                <th className="border border-main px-2 py-2 text-left">
                  Action
                </th>
                {mode.type === "all" && (
                  <th className="border border-main px-2 py-2 text-left">
                    Party
                  </th>
                )}
                <th className="border border-main px-2 py-2 text-left">
                  Details
                </th>
                <th className="border border-main px-2 py-2 text-left">By</th>
              </tr>
            </thead>
            <tbody>
              {filteredChangeLogs.map((log) => (
                <tr key={log._id} className="font-quicksand hover:bg-subtle">
                  <td className="border border-line px-2 py-1.5 whitespace-nowrap text-inkSecondary">
                    {log.at ? formatDateTime(log.at) : "—"}
                  </td>
                  <td className="border border-line px-2 py-1.5 font-semibold uppercase text-xs text-ink">
                    {log.action}
                  </td>
                  {mode.type === "all" && (
                    <td className="border border-line px-2 py-1.5 text-ink">
                      {log.user_type === 1
                        ? `Customer · ${log.customerId?.name || "—"}`
                        : `Company · ${log.companyId?.name || "—"}`}
                    </td>
                  )}
                  <td className="border border-line px-2 py-1.5 text-inkSecondary text-[12px]">
                    {formatLogLine(log)}
                  </td>
                  <td className="border border-line px-2 py-1.5 text-inkSecondary">
                    {log.performedByName || log.performedByName || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <BodyWrapper>
      <Header
        title={title}
        desc="Direct discounts — create / edit / delete every change is logged"
      >
        <button
          type="button"
          className="px-4 py-2 text-sm font-bold border-2 border-main bg-brandSoft text-main rounded-lg hover:bg-main hover:text-white transition-colors font-quicksand active:scale-[.98]"
          onClick={() => load()}
          disabled={loading}
        >
          Refresh
        </button>
        {mode.type !== "all" && (
          <button
            type="button"
            className="px-4 py-2 text-sm font-bold border-2 border-line bg-card text-inkSecondary rounded-lg hover:bg-subtle transition-colors font-quicksand active:scale-[.98]"
            onClick={() => navigate(`${getAppBasePath()}/discount-log`)}
          >
            View all
          </button>
        )}
      </Header>

      <div className="w-full max-w-3xl mb-4 px-2">
        <label className="block text-sm font-bold text-main font-quicksand mb-1">
          Search
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-line bg-card text-ink rounded-lg outline-none focus:border-main focus:ring-2 focus:ring-main/15 font-quicksand"
          placeholder="Search all columns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="w-full max-w-6xl px-2 pb-10">
        {loading ? (
          <div className="flex justify-center py-16">
            <FetchingLoading />
          </div>
        ) : (
          <>
            {renderChangeLogsTable()}
            {renderDiscountTable(filteredRows, "Active discounts", {
              allowActions: true,
            })}
            {(removedRows.length > 0 || filteredRemoved.length > 0) &&
              renderDiscountTable(filteredRemoved, "Removed (history)", {
                allowActions: false,
              })}
          </>
        )}
      </div>

      {editRow && (
        <EditDirectDiscountModal
          Open={!!editRow}
          setOpen={(open) => {
            if (!open) setEditRow(null);
          }}
          discount={editRow}
          onSuccess={load}
        />
      )}

      {deleteRow && (
        <DeleteModal
          Open={!!deleteRow}
          setOpen={(open) => {
            if (!open) setDeleteRow(null);
          }}
          Loading={deleteLoading}
          onSubmit={handleDelete}
        />
      )}
    </BodyWrapper>
  );
};

export default DiscountLogPage;
